import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { buildPackageMap, resolvePcs } from '../../../lib/hpp';

// Nilai default (fallback jika kolom belum ada di DB lama)
const DEFAULT_GAJI_POKOK          = 900000;
const DEFAULT_BONUS_POS_THRESHOLD = 300;
const DEFAULT_BONUS_POS_PER_PCS   = 100;

const fmtRp    = n  => 'Rp ' + (n || 0).toLocaleString('id-ID');
const fmtDate  = d  => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
const fmtShort = d  => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-';
const fmtMonth = ym => new Date(ym + '-01').toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
const thisMonth = () => new Date().toISOString().slice(0, 7);

const S = {
  card:  { background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' },
  muted: { fontSize: '.76rem', color: 'var(--muted)' },
  mono:  { fontFamily: "'DM Mono',monospace" },
  serif: { fontFamily: "'Baloo 2',sans-serif" },
};

const STATUS_MAP = {
  draft:     { label: 'Draft',     bg: '#F5F5F5', c: '#555' },
  confirmed: { label: 'Confirmed', bg: '#E3F2FD', c: '#0D47A1' },
  paid:      { label: 'Lunas',     bg: '#E8F5E9', c: '#1B5E20' },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return (
    <span style={{ background: s.bg, color: s.c, borderRadius: 20, padding: '3px 10px', fontSize: '.7rem', ...S.mono, fontWeight: 500 }}>
      {s.label}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ ...S.mono, fontSize: '.58rem', letterSpacing: '1.8px', textTransform: 'uppercase', color: 'rgba(201,146,42,.7)', marginBottom: 8 }}>
      {children}
    </div>
  );
}

function KPITile({ label, value, sub, accent = '#F8F5F0', valueColor = 'var(--dark)' }) {
  return (
    <div style={{ ...S.card, padding: '14px 18px', background: accent }}>
      <div style={{ ...S.serif, fontSize: '1.25rem', color: valueColor }}>{value}</div>
      <div style={{ ...S.mono, fontSize: '.58rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--muted)', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Btn({ onClick, disabled, children, variant = 'default', small }) {
  const styles = {
    default: { background: 'var(--cream)', color: 'var(--dark)',       border: '1px solid var(--border)' },
    primary: { background: '#CD5226',  color: '#fff',  border: 'none' },
    success: { background: '#1B5E20',      color: 'white',              border: 'none' },
    danger:  { background: '#C62828',      color: 'white',              border: 'none' },
    warning: { background: '#E65100',      color: 'white',              border: 'none' },
  };
  const st = styles[variant] || styles.default;
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...st, padding: small ? '4px 12px' : '7px 16px', borderRadius: 8, fontSize: small ? '.72rem' : '.8rem', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .5 : 1, fontFamily: 'inherit', fontWeight: 500, transition: 'opacity .15s' }}>
      {children}
    </button>
  );
}

// ── Helper: ambil konfigurasi gaji dari data user ───────────────
function getUserGajiConfig(user) {
  return {
    gajiPokok:  user?.gaji_pokok          ?? DEFAULT_GAJI_POKOK,
    threshold:  user?.bonus_pos_threshold  ?? DEFAULT_BONUS_POS_THRESHOLD,
    perPcs:     user?.bonus_pos_per_pcs    ?? DEFAULT_BONUS_POS_PER_PCS,
  };
}

// ── Kalkulasi bonus POS untuk satu user dari data order ─────────
async function calcPosBonus(userId, from, to, threshold, perPcs) {
  const { data: orders } = await supabase
    .from('pos_orders')
    .select('id, created_at')
    .eq('status', 'Selesai')
    .eq('staff_id', userId)
    .gte('created_at', from)
    .lte('created_at', to + 'T23:59:59');

  if (!orders || orders.length === 0) return { rows: [], total: 0 };

  // Catatan: dari data nyata, baris order_items hasil jual PAKET selalu
  // qty=1 (artinya "1 paket", bukan isi paketnya). Supaya bonus dihitung
  // per pcs CIRENG asli (bukan per paket), kita pecah qty paket pakai
  // package_items — lihat lib/hpp.js untuk detail temuannya.
  const [{ data: items }, { data: packages }] = await Promise.all([
    supabase.from('order_items').select('order_id, qty, display_name').in('order_id', orders.map(o => o.id)),
    supabase.from('packages').select('name, package_items(qty)'),
  ]);
  const packageMap = buildPackageMap(packages || []);

  const orderDateMap = {};
  orders.forEach(o => { orderDateMap[o.id] = o.created_at.slice(0, 10); });

  const dayMap = {};
  (items || []).forEach(item => {
    const d = orderDateMap[item.order_id];
    if (d) dayMap[d] = (dayMap[d] || 0) + resolvePcs(item, packageMap);
  });

  const rows = Object.entries(dayMap).sort(([a], [b]) => a.localeCompare(b)).map(([date, totalQty]) => {
    const bonusPcs = Math.max(0, totalQty - threshold);
    const bonusRp  = bonusPcs * perPcs;
    return { date, totalQty, bonusPcs, bonusRp };
  });

  return { rows, total: rows.reduce((s, r) => s + r.bonusRp, 0) };
}

// ── Kalkulasi komisi PO berdasarkan % referral per user ─────────
async function calcPoBonus(userId, from, to, komisiPct) {
  if (!komisiPct || komisiPct <= 0) return { rows: [], total: 0 };

  const { data: poOrders } = await supabase
    .from('po_orders')
    .select('po_id, qty, total, payment_method, payment_status, created_at, po_batches(id, date, status, products(name))')
    .eq('referred_by_user_id', userId)
    .neq('status', 'batal')
    .gte('created_at', from)
    .lte('created_at', to + 'T23:59:59');

  if (!poOrders || poOrders.length === 0) return { rows: [], total: 0 };

  const validOrders = poOrders.filter(o =>
    o.payment_method === 'cod' ||
    (o.payment_method === 'non-tunai' && o.payment_status === 'paid')
  );

  const batchMap = {};
  validOrders.forEach(o => {
    const b = o.po_batches;
    if (!b) return;
    if (!batchMap[b.id]) {
      batchMap[b.id] = {
        date:        b.date,
        batchId:     b.id,
        batchLabel:  fmtShort(b.date) + ' — ' + (b.products?.name || 'Produk PO'),
        productName: b.products?.name || 'Produk PO',
        totalPacks:  0,
        totalOmzet:  0,
        komisiPct,
        bonusRp:     0,
        status:      b.status,
      };
    }
    batchMap[b.id].totalPacks  += o.qty;
    batchMap[b.id].totalOmzet  += o.total || 0;
    batchMap[b.id].bonusRp     += Math.round((o.total || 0) * komisiPct / 100);
  });

  const rows = Object.values(batchMap).sort((a, b) => a.date.localeCompare(b.date));
  return { rows, total: rows.reduce((s, r) => s + r.bonusRp, 0) };
}

// ── Main Component ───────────────────────────────────────────────
export default function BonusKaryawan() {
  const [month, setMonth]             = useState(thisMonth());
  const [users, setUsers]             = useState([]);
  const [records, setRecords]         = useState({});
  const [calcData, setCalcData]       = useState({});
  const [loading, setLoading]         = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);
  const [activeUser, setActiveUser]   = useState(null);
  const [detailTab, setDetailTab]     = useState('pos');
  const [notesEdit, setNotesEdit]     = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  const [toast, setToast]             = useState(null);

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { if (users.length) loadRecordsAndCalc(); }, [month, users]);

  function showToast(msg, color = '#1B5E20') {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 3000);
  }

  async function loadUsers() {
    const { data } = await supabase
      .from('users')
      .select('id, name, role, referral_komisi_pct, gaji_pokok, bonus_pos_threshold, bonus_pos_per_pcs')
      .eq('is_active', true)
      .order('name');
    setUsers(data || []);
  }

  async function loadRecordsAndCalc() {
    setLoading(true);
    const { data: existing } = await supabase
      .from('bonus_payroll')
      .select('*')
      .eq('period', month);

    const rec = {};
    (existing || []).forEach(r => { rec[r.user_id] = r; });
    setRecords(rec);

    const ne = {};
    (existing || []).forEach(r => { ne[r.user_id] = r.notes || ''; });
    setNotesEdit(ne);

    setLoading(false);
  }

  // Kalkulasi fresh — gunakan konfigurasi gaji dari data user
  async function runCalc(userId) {
    setCalcLoading(true);
    const [y, m] = month.split('-');
    const lastDay = new Date(+y, +m, 0).getDate();
    const from    = `${month}-01`;
    const to      = `${month}-${String(lastDay).padStart(2, '0')}`;

    const user      = users.find(u => u.id === userId);
    const { threshold, perPcs } = getUserGajiConfig(user);
    const komisiPct = user?.referral_komisi_pct || 0;

    const [pos, po] = await Promise.all([
      calcPosBonus(userId, from, to, threshold, perPcs),
      calcPoBonus(userId, from, to, komisiPct),
    ]);

    setCalcData(prev => ({ ...prev, [userId]: { posRows: pos.rows, posTotal: pos.total, poRows: po.rows, poTotal: po.total } }));
    setCalcLoading(false);
    return { pos, po };
  }

  async function handleOpenDetail(user) {
    setActiveUser(user);
    setDetailTab('pos');
    if (!calcData[user.id]) {
      await runCalc(user.id);
    }
  }

  async function handleSaveDraft(userId) {
    const user  = users.find(u => u.id === userId);
    const { gajiPokok, threshold, perPcs } = getUserGajiConfig(user);

    const result = await runCalc(userId);
    // Ambil dari hasil kalkulasi langsung (bukan dari state yang mungkin belum di-flush)
    const posTotal = result.pos.total;
    const poTotal  = result.po.total;
    const posRows  = result.pos.rows;
    const poRows   = result.po.rows;
    const total    = gajiPokok + posTotal + poTotal;

    const payload = {
      user_id:             userId,
      user_name:           user?.name || '',
      period:              month,
      gaji_pokok:          gajiPokok,
      bonus_pos_rp:        posTotal,
      bonus_po_rp:         poTotal,
      total_rp:            total,
      detail_pos:          posRows,
      detail_po:           poRows,
      status:              'draft',
      notes:               notesEdit[userId] || '',
      // Simpan snapshot konfigurasi saat perhitungan dilakukan
      snapshot_threshold:  threshold,
      snapshot_per_pcs:    perPcs,
    };

    const { error } = await supabase.from('bonus_payroll').upsert(payload, { onConflict: 'user_id,period' });
    if (error) { showToast('Gagal menyimpan: ' + error.message, '#C62828'); return; }
    showToast('Draft tersimpan untuk ' + user?.name);
    loadRecordsAndCalc();
  }

  async function handleConfirm(userId) {
    const rec = records[userId];
    if (!rec) return;
    const { error } = await supabase.from('bonus_payroll').update({ status: 'confirmed', notes: notesEdit[userId] || rec.notes }).eq('id', rec.id);
    if (error) { showToast('Gagal confirm: ' + error.message, '#C62828'); return; }
    showToast('Bonus dikonfirmasi ✓');
    setConfirmModal(null);
    loadRecordsAndCalc();
  }

  async function handleMarkPaid(userId) {
    const rec = records[userId];
    if (!rec) return;
    const { error } = await supabase.from('bonus_payroll').update({ status: 'paid', paid_at: new Date().toISOString(), notes: notesEdit[userId] || rec.notes }).eq('id', rec.id);
    if (error) { showToast('Gagal update: ' + error.message, '#C62828'); return; }
    showToast('Bonus ditandai Lunas ✓');
    setConfirmModal(null);
    loadRecordsAndCalc();
  }

  async function handleSaveNotes(userId) {
    const rec = records[userId];
    if (!rec) return;
    await supabase.from('bonus_payroll').update({ notes: notesEdit[userId] || '' }).eq('id', rec.id);
    showToast('Catatan disimpan');
    loadRecordsAndCalc();
  }

  // Reset perhitungan bonus — hapus record bonus_payroll bulan ini untuk
  // karyawan tsb, supaya bisa dihitung ulang dari awal (mis. setelah rumus
  // bonus diperbaiki, atau ada koreksi data transaksi). Status confirmed/paid
  // yang biasanya terkunci & tidak bisa "Hitung Ulang" bisa di-reset lewat sini.
  async function handleReset(userId) {
    const rec = records[userId];
    if (!rec) return;
    const { error } = await supabase.from('bonus_payroll').delete().eq('id', rec.id);
    if (error) { showToast('Gagal reset: ' + error.message, '#C62828'); return; }
    setCalcData(prev => { const n = { ...prev }; delete n[userId]; return n; });
    showToast('Perhitungan bonus direset — silakan hitung ulang', '#E65100');
    setConfirmModal(null);
    loadRecordsAndCalc();
  }

  // ── Render helpers ─────────────────────────────────────────────
  function renderUserCard(user) {
    const rec  = records[user.id];
    const calc = calcData[user.id];
    const isActive = activeUser?.id === user.id;
    const hasPoData = (calc?.poTotal || 0) > 0 || (rec?.bonus_po_rp || 0) > 0;
    const { gajiPokok, threshold, perPcs } = getUserGajiConfig(user);

    const posBonus = rec ? rec.bonus_pos_rp : (calc?.posTotal || 0);
    const poBonus  = rec ? rec.bonus_po_rp  : (calc?.poTotal  || 0);
    const total    = rec ? rec.total_rp     : (gajiPokok + posBonus + poBonus);
    const status   = rec?.status || 'draft';
    const hasRec   = !!rec;

    return (
      <div key={user.id} style={{ ...S.card, marginBottom: 12, overflow: 'hidden' }}>
        {/* Header row */}
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', cursor: 'pointer', background: isActive ? '#FDFAF6' : 'white' }}
          onClick={() => { setActiveUser(isActive ? null : user); if (!isActive) handleOpenDetail(user); }}>

          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{user.name}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ ...S.mono, fontSize: '.62rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.8px' }}>{user.role}</span>
              {/* Konfigurasi gaji inline */}
              <span style={{ ...S.mono, fontSize: '.62rem', background: '#F5F0E8', color: 'var(--brown)', borderRadius: 20, padding: '1px 7px' }}>
                {fmtRp(gajiPokok)}/bln
              </span>
              <span style={{ ...S.mono, fontSize: '.62rem', color: 'var(--muted)' }}>
                &gt;{threshold} pcs → +{fmtRp(perPcs)}/pcs
              </span>
              {hasPoData && <span style={{ fontSize: '.62rem', background: '#E3F2FD', color: '#0D47A1', borderRadius: 20, padding: '1px 7px', ...S.mono }}>Ada bonus PO</span>}
              {hasRec && <StatusBadge status={status} />}
            </div>
          </div>

          {/* Angka singkat */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '.68rem', color: 'var(--muted)', ...S.mono, textTransform: 'uppercase', letterSpacing: '.6px' }}>Komisi POS</div>
              <div style={{ fontWeight: 600, color: posBonus > 0 ? '#856404' : 'var(--muted)', ...S.mono, fontSize: '.84rem' }}>{fmtRp(posBonus)}</div>
            </div>
            {(hasPoData || poBonus > 0) && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '.68rem', color: 'var(--muted)', ...S.mono, textTransform: 'uppercase', letterSpacing: '.6px' }}>Komisi PO</div>
                <div style={{ fontWeight: 600, color: poBonus > 0 ? '#1B5E20' : 'var(--muted)', ...S.mono, fontSize: '.84rem' }}>{fmtRp(poBonus)}</div>
              </div>
            )}
            <div style={{ textAlign: 'right', borderLeft: '1px solid var(--border)', paddingLeft: 16 }}>
              <div style={{ fontSize: '.68rem', color: 'var(--muted)', ...S.mono, textTransform: 'uppercase', letterSpacing: '.6px' }}>Total Dibayar</div>
              <div style={{ ...S.serif, fontSize: '1.1rem', color: 'var(--dark)' }}>{fmtRp(total)}</div>
            </div>
          </div>

          <div style={{ fontSize: '1rem', color: 'var(--muted)', marginLeft: 4 }}>{isActive ? '▲' : '▼'}</div>
        </div>

        {/* Expanded detail */}
        {isActive && (
          <div style={{ borderTop: '1px solid var(--border)', padding: '16px 18px', background: '#FAFAF8' }}>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              {!hasRec && (
                <Btn variant="primary" onClick={() => handleSaveDraft(user.id)} disabled={calcLoading}>
                  💾 Hitung &amp; Simpan Draft
                </Btn>
              )}
              {hasRec && status === 'draft' && (
                <>
                  <Btn variant="default" onClick={() => handleSaveDraft(user.id)} disabled={calcLoading} small>🔄 Hitung Ulang</Btn>
                  <Btn variant="warning" onClick={() => setConfirmModal({ type: 'confirm', userId: user.id })}>✓ Konfirmasi Bonus</Btn>
                  <Btn variant="danger" onClick={() => setConfirmModal({ type: 'reset', userId: user.id })} small>↺ Reset</Btn>
                </>
              )}
              {hasRec && status === 'confirmed' && (
                <>
                  <Btn variant="success" onClick={() => setConfirmModal({ type: 'pay', userId: user.id })}>💸 Tandai Lunas</Btn>
                  <Btn variant="danger" onClick={() => setConfirmModal({ type: 'reset', userId: user.id })} small>↺ Reset</Btn>
                </>
              )}
              {hasRec && status === 'paid' && (
                <>
                  <div style={{ fontSize: '.78rem', color: '#1B5E20', fontWeight: 500 }}>
                    ✓ Dibayar {rec.paid_at ? fmtDate(rec.paid_at) : ''}
                  </div>
                  <Btn variant="danger" onClick={() => setConfirmModal({ type: 'reset', userId: user.id })} small>↺ Reset</Btn>
                </>
              )}
            </div>

            {calcLoading && activeUser?.id === user.id && (
              <div style={{ ...S.muted, marginBottom: 12 }}>Menghitung data...</div>
            )}

            {/* Detail tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1.5px solid var(--border)' }}>
              {[['pos', 'Detail POS'], ['po', 'Komisi PO'], ['slip', 'Slip Gaji']].map(([key, lbl]) => (
                key !== 'po' || hasPoData ? (
                  <button key={key} onClick={() => setDetailTab(key)}
                    style={{ padding: '6px 14px', background: 'none', border: 'none', borderBottom: detailTab === key ? '2px solid var(--gold)' : '2px solid transparent', marginBottom: -2, color: detailTab === key ? 'var(--dark)' : 'var(--muted)', fontWeight: detailTab === key ? 600 : 400, fontSize: '.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                    {lbl}
                  </button>
                ) : null
              ))}
            </div>

            {detailTab === 'pos'  && renderPOSDetail(user, rec)}
            {detailTab === 'po'   && renderPODetail(user.id, rec)}
            {detailTab === 'slip' && renderSlip(user, rec, posBonus, poBonus, total, hasPoData)}

            {/* Notes */}
            <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <textarea
                value={notesEdit[user.id] || ''}
                onChange={e => setNotesEdit(p => ({ ...p, [user.id]: e.target.value }))}
                placeholder="Catatan (opsional)..."
                rows={2}
                style={{ flex: 1, minWidth: 200, border: '1.5px solid #DDD', borderRadius: 8, padding: '7px 10px', fontSize: '.8rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
              />
              {hasRec && (
                <Btn small onClick={() => handleSaveNotes(user.id)}>Simpan Catatan</Btn>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // user object diteruskan supaya bisa tampilkan threshold & perPcs yang benar
  function renderPOSDetail(user, rec) {
    const { threshold, perPcs } = getUserGajiConfig(user);
    // Gunakan snapshot jika sudah tersimpan di DB, supaya angka konsisten
    const effectiveThreshold = rec?.snapshot_threshold ?? threshold;
    const effectivePerPcs    = rec?.snapshot_per_pcs    ?? perPcs;

    const rows = rec?.detail_pos?.length ? rec.detail_pos : (calcData[user.id]?.posRows || []);
    if (!rows.length) return <div style={{ ...S.muted, padding: '12px 0' }}>Tidak ada data POS bulan ini untuk karyawan ini.</div>;

    return (
      <div style={{ overflowX: 'auto', marginBottom: 8 }}>
        <div style={{ ...S.muted, marginBottom: 8, fontSize: '.72rem' }}>
          Threshold: <strong>{effectiveThreshold} pcs/hari</strong> · Bonus: <strong>{fmtRp(effectivePerPcs)}/pcs</strong>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', minWidth: 460 }}>
          <thead>
            <tr style={{ background: '#F5F0E8' }}>
              {['Tanggal', 'Terjual', 'Threshold', 'Pcs Bonus', 'Perhitungan', 'Bonus'].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Tanggal' ? 'left' : 'right', ...S.mono, fontSize: '.58rem', letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ background: r.bonusPcs > 0 ? '#FFFDE7' : 'transparent' }}>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6' }}>{fmtShort(r.date)}</td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono }}>{r.totalQty} pcs</td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono, color: 'var(--muted)' }}>{effectiveThreshold}</td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono, color: r.bonusPcs > 0 ? '#856404' : 'var(--muted)' }}>
                  {r.bonusPcs > 0 ? '+' + r.bonusPcs : '—'}
                </td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono, fontSize: '.7rem', color: 'var(--muted)' }}>
                  {r.bonusPcs > 0 ? r.bonusPcs + ' × ' + fmtRp(effectivePerPcs) : '≤ ' + effectiveThreshold}
                </td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono, fontWeight: r.bonusRp > 0 ? 700 : 400, color: r.bonusRp > 0 ? '#856404' : 'var(--muted)' }}>
                  {r.bonusRp > 0 ? fmtRp(r.bonusRp) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: '#FFFDE7' }}>
              <td colSpan={4} style={{ padding: '7px 10px', ...S.mono, fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px' }}>Total Komisi POS</td>
              <td />
              <td style={{ padding: '7px 10px', textAlign: 'right', ...S.mono, fontWeight: 700, color: '#856404' }}>
                {fmtRp(rows.reduce((s, r) => s + r.bonusRp, 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  function renderPODetail(userId, rec) {
    const rows = rec?.detail_po?.length ? rec.detail_po : (calcData[userId]?.poRows || []);
    const userKomisi = users.find(u => u.id === userId)?.referral_komisi_pct || 0;
    if (!rows.length) return (
      <div style={{ ...S.muted, padding: '12px 0' }}>
        Belum ada komisi PO bulan ini.{' '}
        {userKomisi <= 0
          ? <span style={{ color: '#C62828' }}>Atur % komisi di <strong>Master → Pengguna POS</strong> terlebih dahulu.</span>
          : 'Belum ada order via referral yang confirmed/paid.'}
      </div>
    );

    return (
      <div style={{ overflowX: 'auto', marginBottom: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', minWidth: 480 }}>
          <thead>
            <tr style={{ background: '#F0F7F0' }}>
              {['Batch', 'Pack', 'Omzet Referral', '% Komisi', 'Komisi PO'].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Batch' ? 'left' : 'right', ...S.mono, fontSize: '.58rem', letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} onMouseEnter={e => e.currentTarget.style.background = '#FDFAF6'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6' }}>{r.batchLabel}</td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono }}>{r.totalPacks}</td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono }}>{fmtRp(r.totalOmzet)}</td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono, color: 'var(--muted)' }}>
                  {r.komisiPct ?? userKomisi}%
                </td>
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono, fontWeight: 700, color: '#1B5E20' }}>
                  {fmtRp(r.bonusRp)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: '#E8F5E9' }}>
              <td colSpan={3} style={{ padding: '7px 10px', ...S.mono, fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px' }}>Total Komisi PO</td>
              <td />
              <td style={{ padding: '7px 10px', textAlign: 'right', ...S.mono, fontWeight: 700, color: '#1B5E20' }}>
                {fmtRp(rows.reduce((s, r) => s + r.bonusRp, 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  function renderSlip(user, rec, posBonus, poBonus, total, isPO) {
    const { gajiPokok, threshold, perPcs } = getUserGajiConfig(user);
    const status = rec?.status || 'draft';
    return (
      <div style={{ background: 'var(--ink)', borderRadius: 'var(--radius)', padding: '16px 20px', color: 'var(--cream)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div style={{ ...S.mono, fontSize: '.56rem', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--gold-light)', marginBottom: 3 }}>Slip Gaji</div>
            <div style={{ ...S.serif, fontSize: '1.1rem' }}>{user.name}</div>
            <div style={{ fontSize: '.74rem', color: 'rgba(247,242,234,.5)', marginTop: 2 }}>Periode: {fmtMonth(month)}</div>
          </div>
          <StatusBadge status={status} />
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            ['Gaji Pokok',  fmtRp(gajiPokok),  'rgba(247,242,234,.7)'],
            ['Komisi POS',  fmtRp(posBonus),    posBonus > 0 ? 'var(--gold-light)' : 'rgba(247,242,234,.3)'],
            ...(isPO ? [['Komisi PO', fmtRp(poBonus), poBonus > 0 ? '#A5D6A7' : 'rgba(247,242,234,.3)']] : []),
          ].map(([lbl, val, col]) => (
            <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem' }}>
              <span style={{ color: 'rgba(247,242,234,.5)' }}>{lbl}</span>
              <span style={{ ...S.mono, color: col, fontWeight: 500 }}>{val}</span>
            </div>
          ))}
          {/* Info threshold */}
          <div style={{ fontSize: '.68rem', color: 'rgba(247,242,234,.4)', marginTop: 2 }}>
            Bonus POS: &gt;{rec?.snapshot_threshold ?? threshold} pcs/hari × {fmtRp(rec?.snapshot_per_pcs ?? perPcs)}/pcs
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', marginTop: 4, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ ...S.mono, fontSize: '.62rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--gold-light)' }}>Total Dibayar</span>
            <span style={{ ...S.serif, fontSize: '1.3rem', color: 'var(--gold-light)' }}>{fmtRp(total)}</span>
          </div>
          {rec?.paid_at && (
            <div style={{ fontSize: '.72rem', color: 'rgba(247,242,234,.45)', textAlign: 'right' }}>Dibayar: {fmtDate(rec.paid_at)}</div>
          )}
          {rec?.notes && (
            <div style={{ fontSize: '.74rem', color: 'rgba(247,242,234,.5)', marginTop: 4, fontStyle: 'italic' }}>"{rec.notes}"</div>
          )}
        </div>
      </div>
    );
  }

  const paidCount      = Object.values(records).filter(r => r.status === 'paid').length;
  const confirmedCount = Object.values(records).filter(r => r.status === 'confirmed').length;
  const totalBudget    = Object.values(records).reduce((s, r) => s + (r.total_rp || 0), 0);

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: toast.color, color: 'white', padding: '10px 18px', borderRadius: 10, zIndex: 1000, fontSize: '.84rem', fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,.2)' }}>
          {toast.msg}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ ...S.card, padding: '24px 28px', maxWidth: 380, width: '100%' }}>
            {confirmModal.type === 'confirm' ? (
              <>
                <div style={{ ...S.serif, fontSize: '1.1rem', marginBottom: 8 }}>Konfirmasi Bonus?</div>
                <div style={{ ...S.muted, marginBottom: 20, fontSize: '.84rem', lineHeight: 1.5 }}>
                  Setelah dikonfirmasi, angka bonus akan dikunci dan tidak bisa dihitung ulang. Pastikan data sudah benar.
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn onClick={() => setConfirmModal(null)}>Batal</Btn>
                  <Btn variant="warning" onClick={() => handleConfirm(confirmModal.userId)}>Ya, Konfirmasi</Btn>
                </div>
              </>
            ) : confirmModal.type === 'reset' ? (
              <>
                <div style={{ ...S.serif, fontSize: '1.1rem', marginBottom: 8 }}>Reset Perhitungan Bonus?</div>
                <div style={{ ...S.muted, marginBottom: 12, fontSize: '.84rem', lineHeight: 1.5 }}>
                  Ini akan menghapus catatan perhitungan bonus bulan ini untuk karyawan tsb (termasuk status & catatan), supaya bisa dihitung ulang dari awal.
                </div>
                {records[confirmModal.userId]?.status === 'paid' && (
                  <div style={{ background: '#FFEBEE', color: '#C62828', borderRadius: 8, padding: '9px 12px', fontSize: '.78rem', lineHeight: 1.5, marginBottom: 12 }}>
                    ⚠️ Bonus ini sudah ditandai <strong>Lunas</strong>. Reset hanya menghapus catatan perhitungan di sistem — <strong>tidak membatalkan</strong> uang yang sudah dibayarkan ke karyawan. Pastikan Anda tahu persis alasannya sebelum lanjut.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn onClick={() => setConfirmModal(null)}>Batal</Btn>
                  <Btn variant="danger" onClick={() => handleReset(confirmModal.userId)}>Ya, Reset</Btn>
                </div>
              </>
            ) : (
              <>
                <div style={{ ...S.serif, fontSize: '1.1rem', marginBottom: 8 }}>Tandai Sudah Dibayar?</div>
                <div style={{ ...S.muted, marginBottom: 20, fontSize: '.84rem', lineHeight: 1.5 }}>
                  Ini akan mencatat tanggal pembayaran hari ini. Aksi ini tidak bisa dibatalkan.
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn onClick={() => setConfirmModal(null)}>Batal</Btn>
                  <Btn variant="success" onClick={() => handleMarkPaid(confirmModal.userId)}>Ya, Sudah Dibayar</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ ...S.serif, fontSize: '1.5rem' }}>Gaji Karyawan</div>
          <div style={{ ...S.muted }}>Rekap & pembayaran gaji per bulan</div>
        </div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          style={{ border: '1.5px solid #DDD', borderRadius: 8, padding: '6px 10px', fontSize: '.84rem', outline: 'none', fontFamily: 'inherit' }}
          onFocus={e => e.target.style.borderColor = 'var(--gold)'}
          onBlur={e  => e.target.style.borderColor = '#DDD'} />
      </div>

      {/* Info */}
      <div style={{ ...S.card, padding: '12px 18px', marginBottom: 20, background: '#FDFAF6', display: 'flex', gap: 8, alignItems: 'center', fontSize: '.8rem' }}>
        <span style={{ fontSize: '1rem' }}>ℹ️</span>
        <span>
          Gaji pokok, threshold POS, dan bonus per pcs diambil dari konfigurasi masing-masing karyawan.
          Ubah di <strong>Master → Pengguna POS</strong>.
          Komisi PO dihitung dari omzet order via referral × % komisi karyawan.
        </span>
      </div>

      {/* KPI overview */}
      {Object.keys(records).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 20 }}>
          <KPITile label="Total Budget Gaji" value={fmtRp(totalBudget)} accent="#F0EBE3" />
          <KPITile label="Sudah Dikonfirmasi" value={confirmedCount + ' orang'} accent="#E3F2FD" valueColor="#0D47A1" />
          <KPITile label="Sudah Dibayar" value={paidCount + ' orang'} accent="#E8F5E9" valueColor="#1B5E20" />
          <KPITile label="Belum Selesai" value={(users.length - paidCount) + ' orang'} accent="#FFF3E0" valueColor="#E65100" />
        </div>
      )}

      {/* User cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--muted)' }}>Memuat data...</div>
      ) : users.length === 0 ? (
        <div style={{ ...S.card, padding: 32, textAlign: 'center', ...S.muted }}>Belum ada karyawan aktif. Tambahkan di Master → Pengguna POS.</div>
      ) : (
        <div>
          <SectionLabel>Karyawan Aktif — {fmtMonth(month)}</SectionLabel>
          {users.map(u => renderUserCard(u))}
        </div>
      )}
    </div>
  );
}
